import { useEffect, useMemo, useState } from 'react';
import { subscribeLive } from './useLiveEvents.js';

/**
 * Contadores que o menu mostra ao lado dos itens.
 *
 * Por que existe: num menu de trinta itens, todos com o mesmo peso visual,
 * "Alertas" parece tão urgente quanto "Configurações". O operador só
 * descobre que há vinte e um alertas abertos depois de navegar até lá.
 * Com o número no próprio menu, a lista vira painel.
 *
 * Só contamos o que exige ação humana: alerta em aberto e ordem de
 * manutenção não concluída. Nada de contar "sessões" ou "carregadores",
 * que são volume, não pendência — badge em tudo não sinaliza nada.
 *
 * ## Três estados, não dois
 *
 * O contador do menu é a ÚNICA informação de pendência visível quando o
 * menu está colapsado. Por isso ele não pode ter só "tem" e "não tem": um
 * contador que devolve zero quando a leitura falhou afirma "nada pendente"
 * sem ter lido nada — e a sessão expirando numa aba aberta produz
 * exatamente isso (tudo vira 401, o ponto apaga, ninguém é avisado).
 *
 *   - `carregando` — ainda não houve resposta. Não desenha nada. Dura uma
 *     ida à rede; não afirma coisa nenhuma.
 *   - `ok`         — leitura viva. `n` é o número de verdade (podendo ser 0).
 *   - `sem-sinal`  — a leitura falhou, ou envelheceu além do limiar. `n` é
 *     o ÚLTIMO valor conhecido (ou `null` se nunca houve um) e a UI degrada
 *     conforme o padrão STALE do CLAUDE.md, com a cor de aviso em vez da
 *     cor de alarme.
 */

/** Estado de uma contagem. Ver o bloco acima. */
export type EstadoContagem = 'carregando' | 'ok' | 'sem-sinal';

export interface ContagemNav {
  /** Última contagem conhecida. `null` = nunca houve leitura bem-sucedida. */
  n: number | null;
  /** Se `n` pode ser apresentado como leitura viva. */
  estado: EstadoContagem;
}

/** Nada lido ainda. Não é zero: é ausência de resposta. */
const CARREGANDO: ContagemNav = { n: null, estado: 'carregando' };

/** Zero afirmado — para os módulos que simplesmente não têm pendência
 *  contável (Dashboard, Configurações...). Diferente de `CARREGANDO`:
 *  aqui sabemos que não há nada a mostrar. */
export const SEM_PENDENCIA: ContagemNav = { n: 0, estado: 'ok' };

export interface NavBadges {
  alerts: ContagemNav;
  maintenance: ContagemNav;
}

/** Estado sem contador. Exportado porque o `Sidebar` precisa de um default
 *  para a prop `badges`: o polling mora no `AppShell` (uma vez para as duas
 *  instâncias do menu), não no componente. */
export const NAV_BADGES_VAZIO: NavBadges = {
  alerts: CARREGANDO,
  maintenance: CARREGANDO,
};

/**
 * Varredura de fundo. 60s: pendência não muda em segundos, e o menu não
 * pode competir com as telas pelo orçamento de rede. Não é o caminho
 * principal de atualização — é a rede de segurança para o que o push ao
 * vivo não cobre (alerta resolvido em outra aba, ordem fechada por outro
 * usuário).
 */
const INTERVALO_MS = 60_000;

/**
 * Piso entre duas idas à rede, qualquer que seja o gatilho (intervalo,
 * `visibilitychange`, push do WS, revalidação explícita). Sem isso, dez
 * trocas de aba em um segundo e meio viravam vinte requisições em dois
 * endpoints sem rate limit. Rajada agora coalesce em uma leitura imediata
 * mais, no máximo, uma de arrasto.
 */
const MIN_ENTRE_LEITURAS_MS = 10_000;

/**
 * Idade a partir da qual o número deixa de ser apresentado como vivo.
 * 2,5× o intervalo: perder um ciclo é rede lenta, perder dois é contador
 * que ninguém está mantendo (aba escondida há muito tempo, servidor
 * respondendo mas nunca renovando). Passando daqui, o menu diz "sem
 * sinal" em vez de repetir um número vencido com cara de atual.
 */
const LIMIAR_STALE_MS = 150_000;

/**
 * Teto de espera de UMA leitura. Sem ele, `fetch` só termina quando a
 * conexão termina — e uma requisição **pendurada** (proxy que aceita e
 * nunca responde, túnel meio-aberto) nunca vira falha. O relógio de
 * `LIMIAR_STALE_MS` só é armado DEPOIS de uma resposta boa, então com a
 * PRIMEIRA leitura pendurada o contador ficava em `carregando` para sempre:
 * sem badge, sem ponto e sem "sem sinal" — menu sem ponto lê-se como pátio
 * calmo, que é exatamente "não sabemos" se apresentando como "nada
 * pendente". 10s: mesma ordem do piso entre leituras e bem abaixo dos 60s
 * do intervalo. Igual ao piso, os dois se tocam no limite — uma leitura
 * pendurada aborta em t=10s e um `visibilitychange` em t=10s já é admitido
 * — mas a leitura que aborta já terminou, então não há duas em voo.
 */
const TIMEOUT_LEITURA_MS = 10_000;

/** Leitura crua de um endpoint. Não deriva nada — só o que voltou da rede. */
interface Leitura {
  /** Última contagem conhecida; `null` enquanto nunca houve uma. */
  n: number | null;
  /** A última tentativa falhou (rede caída, 401, 403, 500, JSON torto). */
  falhou: boolean;
}

const LEITURA_INICIAL: Leitura = { n: null, falhou: false };

/**
 * Conta itens de uma lista da API.
 *
 * Devolve `null` — e não `0` — em qualquer falha. Zero é uma afirmação
 * ("não há nada pendente") que só se pode fazer depois de ler a lista;
 * rede caída, sessão expirada, 500 do servidor e leitura **pendurada** além
 * de `TIMEOUT_LEITURA_MS` não autorizam essa afirmação. Quem chama decide
 * o que mostrar sem saber.
 *
 * `AbortController` + `setTimeout`, e não `AbortSignal.timeout`, por dois
 * motivos medidos:
 *
 *  1. **Alvo de build.** O projeto não tem `browserslist` e o Vite não
 *     define `build.target`, então vale o default `'modules'` — que inclui
 *     Safari 14. `AbortSignal.timeout` só existe a partir do Safari 16, e
 *     lá a chamada lançaria `TypeError` DENTRO deste `try`: o `catch`
 *     engoliria, `contar` devolveria `null` SEMPRE e os dois contadores
 *     ficariam presos em "sem sinal" com a rede perfeita — a mesma
 *     afirmação falsa que os três estados existem para evitar, invertida.
 *  2. **Testabilidade.** `vi.useFakeTimers()` não controla o relógio de
 *     `AbortSignal.timeout` (o sinal não aborta ao adiantar o tempo), mas
 *     controla `setTimeout`. Com o controller, o teste do timeout custa
 *     milissegundos em vez de 10s reais.
 */
async function contar(url: string, chave: string): Promise<number | null> {
  const controle = new AbortController();
  const timer = setTimeout(() => controle.abort(), TIMEOUT_LEITURA_MS);
  try {
    const r = await fetch(url, { credentials: 'include', signal: controle.signal });
    if (!r.ok) return null;
    const j = (await r.json()) as Record<string, unknown>;
    const lista = j[chave];
    return Array.isArray(lista) ? lista.length : null;
  } catch {
    // Inclui o `AbortError` do estouro: pendurar passa a ser falha, e falha
    // já sabemos apresentar ("sem sinal"), diferente de `carregando` eterno.
    return null;
  } finally {
    // Sem isto, cada leitura boa deixaria um timer de 10s pendurado — em
    // 60s de intervalo por aba isso é lixo constante no event loop.
    clearTimeout(timer);
  }
}

/** Exposto só para teste (mesmo padrão de `_internals` do servidor).
 *  `contar` não é API deste módulo — não importe em componente. */
export const _internals = { contar, TIMEOUT_LEITURA_MS };

/** Aplica o resultado de uma tentativa sobre a leitura anterior. Devolve o
 *  MESMO objeto quando nada observável mudou, para o React poder abortar o
 *  re-render: em regime estável o menu não redesenha a cada 60s. */
function aplicar(prev: Leitura, valor: number | null): Leitura {
  if (valor === null) return prev.falhou ? prev : { ...prev, falhou: true };
  if (!prev.falhou && prev.n === valor) return prev;
  return { n: valor, falhou: false };
}

function derivar(l: Leitura, vencido: boolean): ContagemNav {
  if (l.n === null) return l.falhou ? { n: null, estado: 'sem-sinal' } : CARREGANDO;
  if (l.falhou || vencido) return { n: l.n, estado: 'sem-sinal' };
  return { n: l.n, estado: 'ok' };
}

/**
 * Combina as contagens dos itens de uma categoria numa só — é isso que o
 * trilho colapsado mostra como ponto.
 *
 * A soma só vale se TODAS as partes são vivas: com um membro sem sinal, o
 * total é desconhecido, e apresentá-lo como número fechado seria a mesma
 * mentira que o contador antigo contava. Precedência:
 * `sem-sinal` > `carregando` > `ok`.
 */
export function combinarContagens(partes: ContagemNav[]): ContagemNav {
  let estado: EstadoContagem = 'ok';
  let soma = 0;
  // Uma parcela sem número torna o TOTAL desconhecido. Sem isto, uma
  // categoria com Alertas nunca lido (null) e Sessões sem pendência (0)
  // somaria 0 e o menu anunciaria "último: 0 em aberto" — a mesma afirmação
  // falsa que estamos removendo, só que agregada.
  let temDesconhecida = false;
  for (const p of partes) {
    if (p.estado === 'sem-sinal') estado = 'sem-sinal';
    else if (p.estado === 'carregando' && estado !== 'sem-sinal') estado = 'carregando';
    if (p.n === null) temDesconhecida = true;
    else soma += p.n;
  }
  return { n: temDesconhecida ? null : soma, estado };
}

/** Ouvintes de revalidação. Módulo, não contexto: quem muda uma pendência
 *  (resolver alerta, fechar ordem) não precisa estar na árvore do menu. */
const ouvintes = new Set<() => void>();

/**
 * Força uma releitura dos contadores do menu, respeitando o throttle.
 *
 * Chamar depois de MUDAR uma pendência. O push do WS só anuncia alerta
 * NOVO (`alert.created`); quando o operador resolve um alerta ou fecha uma
 * ordem, o número cai e ninguém avisa o menu — era assim que a tela dizia
 * "tudo tranquilo" com o badge ao lado ainda marcando 3.
 */
export function revalidarNavBadges(): void {
  for (const f of ouvintes) f();
}

export function useNavBadges(enabled: boolean): NavBadges {
  const [leituras, setLeituras] = useState<{ alerts: Leitura; maintenance: Leitura }>({
    alerts: LEITURA_INICIAL,
    maintenance: LEITURA_INICIAL,
  });
  // Idade é estado, não cálculo de render: sem um re-render no momento em
  // que o número vence, o menu continuaria exibindo valor velho como vivo
  // até alguém mexer na tela.
  const [vencido, setVencido] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let cancelado = false;
    let ultimaIda = 0;
    let timerThrottle: ReturnType<typeof setTimeout> | null = null;
    let timerVencimento: ReturnType<typeof setTimeout> | null = null;

    const buscar = async () => {
      ultimaIda = Date.now();
      const [alerts, maintenance] = await Promise.all([
        // `onlyOpen=1`, não `status=open`: é o parâmetro que
        // `GET /api/alerts` realmente lê (o mesmo que a AlertsPage manda).
        // Com `status` a rota ignorava o filtro e devolvia a tabela inteira
        // — 26 no badge contra 21 abertos na tela.
        contar('/api/alerts?onlyOpen=1', 'alerts'),
        contar('/api/maintenance?status=open', 'orders'),
      ]);
      if (cancelado) return;
      setLeituras((prev) => {
        const a = aplicar(prev.alerts, alerts);
        const m = aplicar(prev.maintenance, maintenance);
        return a === prev.alerts && m === prev.maintenance ? prev : { alerts: a, maintenance: m };
      });
      // Qualquer resposta boa reinicia o relógio de vencimento.
      if (alerts !== null || maintenance !== null) {
        setVencido(false);
        if (timerVencimento) clearTimeout(timerVencimento);
        timerVencimento = setTimeout(() => {
          if (!cancelado) setVencido(true);
        }, LIMIAR_STALE_MS);
      }
    };

    /** Pede uma leitura. Executa na hora se o piso já passou; senão marca
     *  uma única leitura de arrasto e descarta o resto da rajada. */
    const agendar = () => {
      if (cancelado || document.hidden) return;
      const desde = Date.now() - ultimaIda;
      if (desde >= MIN_ENTRE_LEITURAS_MS) {
        void buscar();
        return;
      }
      if (timerThrottle) return;
      timerThrottle = setTimeout(() => {
        timerThrottle = null;
        if (!cancelado && !document.hidden) void buscar();
      }, MIN_ENTRE_LEITURAS_MS - desde);
    };

    void buscar();
    const t = setInterval(agendar, INTERVALO_MS);
    document.addEventListener('visibilitychange', agendar);
    // Push ao vivo na MESMA conexão que a AlertsPage já consome: alerta
    // novo aparece no menu em segundos, sem uma requisição a mais e sem
    // encurtar o intervalo de varredura para todo mundo.
    const desassinar = subscribeLive('alert.created', agendar);
    ouvintes.add(agendar);

    return () => {
      cancelado = true;
      clearInterval(t);
      if (timerThrottle) clearTimeout(timerThrottle);
      if (timerVencimento) clearTimeout(timerVencimento);
      document.removeEventListener('visibilitychange', agendar);
      desassinar();
      ouvintes.delete(agendar);
    };
  }, [enabled]);

  return useMemo<NavBadges>(
    () => ({
      alerts: derivar(leituras.alerts, vencido),
      maintenance: derivar(leituras.maintenance, vencido),
    }),
    [leituras, vencido],
  );
}
